import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  BookOpen, Search, Download, CheckCircle, FileText, 
  Upload, RefreshCw, ExternalLink
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function Training() {
  const { api, isAdmin, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    category: 'General',
    version: '1.0',
    required_for: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [filterCategory]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      const response = await api.get('/training', { params });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch training documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', uploadData.title);
    formData.append('category', uploadData.category);
    formData.append('version', uploadData.version);
    formData.append('required_for', uploadData.required_for);

    try {
      await api.post('/training/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ title: '', category: 'General', version: '1.0', required_for: '' });
      setSelectedFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/training/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleMarkComplete = async (doc) => {
    try {
      await api.post(`/training/${doc.id}/complete`);
      toast.success('Training marked as complete');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as complete');
    }
  };

  const categories = [...new Set(documents.map(d => d.category))];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const completedCount = documents.filter(d => d.completed).length;
  const requiredDocs = documents.filter(d => d.required_for?.includes(user?.role));
  const completedRequired = requiredDocs.filter(d => d.completed).length;

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Training Library</h1>
          <p className="text-slate-500 mt-1">Access training materials and track your progress</p>
        </div>
        {isAdmin && (
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-training-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Progress Card */}
      <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Your Training Progress</h3>
              <p className="text-slate-600 mt-1">
                {completedCount} of {documents.length} documents completed
                {requiredDocs.length > 0 && (
                  <span className="ml-2">
                    • {completedRequired}/{requiredDocs.length} required for your role
                  </span>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-teal-600">
                {documents.length > 0 ? Math.round((completedCount / documents.length) * 100) : 0}%
              </p>
              <p className="text-sm text-slate-500">Complete</p>
            </div>
          </div>
          {documents.length > 0 && (
            <div className="mt-4 h-2 bg-white rounded-full overflow-hidden">
              <div 
                className="h-full bg-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${(completedCount / documents.length) * 100}%` }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-training"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">No training documents found</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredDocuments.map((doc) => (
                <Card 
                  key={doc.id} 
                  className={`hover:shadow-md transition-shadow ${doc.completed ? 'border-emerald-200 bg-emerald-50/50' : ''}`}
                  data-testid={`training-doc-${doc.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          doc.completed ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          {doc.completed ? (
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">{doc.title}</h4>
                          <p className="text-sm text-slate-500">{doc.category}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">v{doc.version}</Badge>
                    </div>

                    {doc.required_for?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-slate-500">Required for:</p>
                        <div className="flex gap-1 mt-1">
                          {doc.required_for.map(role => (
                            <Badge 
                              key={role} 
                              variant="outline" 
                              className={`text-xs ${role === user?.role ? 'border-teal-500 text-teal-700' : ''}`}
                            >
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {doc.completed && doc.completed_at && (
                      <p className="mt-3 text-xs text-emerald-600">
                        Completed on {format(parseISO(doc.completed_at), 'MMM d, yyyy')}
                      </p>
                    )}

                    <div className="mt-4 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-btn-${doc.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      {!doc.completed && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          onClick={() => handleMarkComplete(doc)}
                          data-testid={`complete-btn-${doc.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upload Training Document</DialogTitle>
            <DialogDescription>Add a new training document to the library</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  placeholder="Document title"
                  required
                  data-testid="upload-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    value={uploadData.category}
                    onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                    placeholder="e.g., Safety"
                    data-testid="upload-category"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    value={uploadData.version}
                    onChange={(e) => setUploadData({ ...uploadData, version: e.target.value })}
                    placeholder="1.0"
                    data-testid="upload-version"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Required For (comma-separated roles)</Label>
                <Input
                  value={uploadData.required_for}
                  onChange={(e) => setUploadData({ ...uploadData, required_for: e.target.value })}
                  placeholder="employee,physician"
                  data-testid="upload-required-for"
                />
              </div>
              <div className="space-y-2">
                <Label>File (PDF)</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  data-testid="upload-file"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={uploadLoading}
                data-testid="upload-submit"
              >
                {uploadLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
